import {
  IssueState,
  IssueResolution,
  RunStatus,
  StageStatus,
  PullRequestState,
  type PrototypeDeployStatus,
} from '@aiai/shared';

/** The six badge tones. success=emerald, warning=amber, danger=red, info=sky. */
export type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

export const RUN_STATUS_TONE: Record<RunStatus, Tone> = {
  [RunStatus.Pending]: 'neutral',
  [RunStatus.Provisioning]: 'brand',
  [RunStatus.Running]: 'brand',
  [RunStatus.Succeeded]: 'success',
  [RunStatus.Failed]: 'danger',
  [RunStatus.Cancelled]: 'neutral',
  [RunStatus.TimedOut]: 'danger',
};

export const ISSUE_STATE_TONE: Record<IssueState, Tone> = {
  [IssueState.Proposed]: 'warning', // 🟠 needs you (approve)
  [IssueState.Open]: 'info',
  [IssueState.Building]: 'brand',
  [IssueState.Review]: 'warning', // 🟠 needs you (merge)
  [IssueState.Failed]: 'danger',
  [IssueState.Closed]: 'success', // terminal "done" — green, not a dead gray
};

export const RESOLUTION_TONE: Record<IssueResolution, Tone> = {
  [IssueResolution.Fixed]: 'success',
  [IssueResolution.WontDo]: 'neutral',
  [IssueResolution.Duplicate]: 'neutral',
  [IssueResolution.Planned]: 'info',
  [IssueResolution.Decomposed]: 'neutral',
};

export const STAGE_STATUS_TONE: Record<StageStatus, Tone> = {
  [StageStatus.Pending]: 'neutral',
  [StageStatus.Running]: 'brand',
  [StageStatus.Succeeded]: 'success',
  [StageStatus.Failed]: 'danger',
  [StageStatus.Skipped]: 'neutral',
  [StageStatus.AwaitingGate]: 'warning',
};

export const PR_STATE_TONE: Record<PullRequestState, Tone> = {
  [PullRequestState.None]: 'neutral',
  [PullRequestState.Open]: 'brand',
  [PullRequestState.Merged]: 'success',
  [PullRequestState.Closed]: 'neutral',
};

export const DEPLOY_STATUS_TONE: Record<PrototypeDeployStatus, Tone> = {
  provisioning: 'brand',
  building: 'brand',
  live: 'success',
  failed: 'danger',
  stopped: 'neutral',
  sleeping: 'neutral',
};

/** Calm-motion rule: every non-terminal in-flight state pulses; nothing else animates. */
const PULSING: ReadonlySet<string> = new Set([
  RunStatus.Provisioning,
  RunStatus.Running, // same string as StageStatus.Running
  'building',
]);

export function isPulsing(status: string): boolean {
  return PULSING.has(status);
}

/**
 * Combined lookup for components that accept the status union. Safe because
 * the per-enum maps agree on every shared string value (pinned by tests).
 * Built once at module scope rather than per call.
 */
const MERGED_STATUS_TONE: Record<string, Tone> = {
  ...RUN_STATUS_TONE,
  ...ISSUE_STATE_TONE,
  ...STAGE_STATUS_TONE,
  ...DEPLOY_STATUS_TONE,
};

export function statusTone(
  status: RunStatus | IssueState | StageStatus | PrototypeDeployStatus,
): Tone {
  return MERGED_STATUS_TONE[status] ?? 'neutral';
}
